import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../environments/environment';

const HOST = environment.serverUrl;
const CHARGEPOINTS_API_URL = HOST + '/chargepoints';

export enum ChargepointType {
  OCPP = 'OCPP',
  OKECHARGE = 'OKECHARGE',
  IVY = 'IVY',
  NETATMO = 'NETATMO'
}

export enum ChargepointStatus {
  CONFIGURING = 'CONFIGURING',
  AVAILABLE = 'AVAILABLE',
  PREPARING = 'PREPARING',
  CHARGING = 'CHARGING',
  FINISHING = 'FINISHING',
  UNREACHABLE = 'UNREACHABLE'
}

export interface AbstractChargepoint {
  id: string;
  type: ChargepointType;
  status: ChargepointStatus;
  lastHeartbeat?: Date;
}

export interface Connector {
  connectorId: number;
  status: ChargepointStatus;
}

export interface LocalAuthorizationList {
  supported: boolean;
  enabled: boolean;
  identifiers: string[];
  synchronizationDone: boolean;
}

export interface OCPPChargepoint extends AbstractChargepoint {
  chargePointVendor: string;
  chargePointModel: string;
  chargePointSerialNumber: string;
  firmwareVersion: string;
  connectors: {[key: string]: Connector};
  localAuthorizationList?: LocalAuthorizationList;
}

export interface OkechargeChargepoint extends AbstractChargepoint {
  ssid: string;
  version: string;
  chipId: string;
}

export interface IvyChargepoint extends AbstractChargepoint {
  plugId: string;
  specificStatus?: string;
}

export interface NetatmoChargepoint extends AbstractChargepoint {
  plugId: string;
  plugName: string;
  bridgeId: string;
  homeId: string;
  homeName: string;
}

export type Chargepoint = OCPPChargepoint | OkechargeChargepoint | IvyChargepoint | NetatmoChargepoint;

function reviveChargepoint(chargepoint: Chargepoint): Chargepoint {
  chargepoint.lastHeartbeat = chargepoint.lastHeartbeat ? new Date(chargepoint.lastHeartbeat) : undefined;
  return chargepoint;
}

export interface ChargepointSearchCriteria {
  type: ChargepointType | null;
  id: string | null;
}

@Injectable({ providedIn: 'root' })
export class ChargepointsApiService {

  constructor(private http: HttpClient) { }

  public searchChargepoints(searchCriteria: ChargepointSearchCriteria): Observable<Chargepoint[]> {
    return this.http.post<Chargepoint[]>(`${CHARGEPOINTS_API_URL}/search`, {...searchCriteria}, { responseType: 'json' })
      .pipe(
        map(chargepoints => chargepoints.map(reviveChargepoint))
      );
  }

  public getChargepoint(id: string): Observable<Chargepoint> {
    return this.http.get<Chargepoint>(`${CHARGEPOINTS_API_URL}/${id}`).pipe(
      map(chargepoint => reviveChargepoint(chargepoint))
    );
  }

}
